import { IconDefinition } from '@hg-akademie-ban/ban-elevate-assets';
import { faCircleCheck, faCircleQuestion, faCircleX } from '@hg-akademie-ban/ban-elevate-assets/icons/regular';
import { Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import { Icon as BanRegistryIcon } from '@/components/ui/icon.tsx';

import { Antwort, type Leistung } from '../../dtos.ts';
import { content } from '../../utils/index.ts';
import { getAllLeistungen, getBestAntwort } from '../../utils/teilleistung.ts';
import { content as rückmeldungContent } from './TableColumnsRückmeldung.content.ts';

type Result = 'none' | 'error' | 'question' | 'success';
type TeilleistungenHolder = { teilleistungen: { leistung: Leistung }[] };

const resultByAntwort = {
  keineAntwort: 'none',
  ausschreibungAngenommen: 'success',
  ausschreibungUnterVorbehaltAngenommen: 'question',
  ausschreibungAbgelehnt: 'error',
  ausschreibungAbgelaufen: 'error',
  angebotAngenommen: 'success',
  angebotAbgelehnt: 'error',
  angebotAbgelaufen: 'error',
} as const satisfies Record<Antwort, Result>;

const titleByAntwort = {
  keineAntwort: rückmeldungContent.keineRückmeldung,
  ausschreibungAbgelehnt: rückmeldungContent.ausschreibungAbgelehnt,
  ausschreibungAbgelaufen: rückmeldungContent.ausschreibungAbgelehnt,
  angebotAbgelehnt: rückmeldungContent.angebotAbgelehnt,
  angebotAbgelaufen: rückmeldungContent.angebotAbgelehnt,
  ausschreibungUnterVorbehaltAngenommen: rückmeldungContent.antwortMitVorbehalt,
  ausschreibungAngenommen: rückmeldungContent.ausschreibungAngenommen,
  angebotAngenommen: rückmeldungContent.angebotAngenommen,
} as const satisfies Record<Antwort, string>;

const AntwortDash = function () {
  return <span title={rückmeldungContent.keineRückmeldung}>-</span>;
};

const iconByResult = {
  error: { icon: faCircleX, className: 'text-feedback-error' },
  question: { icon: faCircleQuestion, className: 'text-secondary' },
  success: { icon: faCircleCheck, className: 'text-feedback-success' },
} as const satisfies Record<Exclude<Result, 'none'>, { icon: IconDefinition; className: string }>;

const AntwortIcon = function ({ result, title }: { result: Exclude<Result, 'none'>; title: string }) {
  return (
    <span title={title}>
      <BanRegistryIcon size="default" {...iconByResult[result]} />
    </span>
  );
};

export function veranstaltungMultipleRückmeldungenColumn<T extends TeilleistungenHolder>(): GridColDef<T, Antwort[]> {
  return {
    field: 'rückmeldung',
    flex: 0.5,
    renderHeader,
    valueGetter: (_, row) => getAllRückmeldungen(row.teilleistungen),
    renderCell: ({ value }) => (value ? renderMultipleRückmeldungen(value) : null),
  };
}

export function durchgangMultipleRückmeldungColumn<T extends { modultermine: TeilleistungenHolder[] }>(): GridColDef<
  T,
  Antwort[]
> {
  return {
    field: 'rückmeldung',
    flex: 0.5,
    renderHeader,
    valueGetter: (_, row) => getAllRückmeldungen(row.modultermine.flatMap((modultermin) => modultermin.teilleistungen)),
    renderCell: ({ value }) => renderMultipleRückmeldungen(value),
  };
}

export function modulterminSingleRückmeldungColumn<T extends { leistung?: Leistung }>(): GridColDef<
  T,
  Antwort | undefined
> {
  return {
    field: 'rückmeldung',
    flex: 0.5,
    renderHeader,
    valueGetter: (_, row) => getBestAntwort(row.leistung),
    renderCell: ({ value }) => renderSingleRückmeldung(value),
  };
}

export function leistungSingleRückmeldungColumn<T extends Leistung>(): GridColDef<T, Antwort | undefined> {
  return {
    field: 'rückmeldung',
    flex: 0.5,
    renderHeader,
    valueGetter: (_, row) => getBestAntwort(row),
    renderCell: ({ value }) => renderSingleRückmeldung(value),
  };
}

function renderHeader() {
  return (
    <Tooltip title={content.tooltips.antwortAP}>
      <span>{content.columnLabels.antwortAP}</span>
    </Tooltip>
  );
}

export function renderSingleRückmeldung(antwort: Antwort | undefined) {
  if (!antwort) {
    return <AntwortDash />;
  }

  const title = titleByAntwort[antwort];
  const result = resultByAntwort[antwort];

  return result === 'none' ? <AntwortDash /> : <AntwortIcon result={result} title={title} />;
}

export function renderMultipleRückmeldungen(antworten: Antwort[] | undefined) {
  const results = antworten?.map((antwort) => resultByAntwort[antwort]) ?? [];

  return (
    <>
      {(results.includes('none') || results.length === 0) && (
        <span className="mr-4">
          <AntwortDash />
        </span>
      )}
      {results.includes('error') && (
        <AntwortIcon title={rückmeldungContent.ausschreibungOderAngebotAbgelehnt} result="error" />
      )}
      {results.includes('question') && <AntwortIcon title={rückmeldungContent.antwortMitVorbehalt} result="question" />}
      {results.includes('success') && (
        <AntwortIcon title={rückmeldungContent.ausschreibungOderAngebotAngenommen} result="success" />
      )}
    </>
  );
}

function getAllRückmeldungen(teilleistungen: readonly { leistung: Leistung }[]): Antwort[] {
  return getAllLeistungen(teilleistungen).map(getBestAntwort);
}
